import { removeStoredMutationsById } from '../graphql/util/removeStoredMutationsById'
import { scheduleDelayedRefetch } from '../graphql/util/scheduleDelayedRefetch'
import type { Task } from '@paintscout/api'
import {
  updateCachedTask,
  updateCachedTaskPartially,
  useDeleteTasksMutation,
  useNotifyChecklistAssigneesMutation,
  useSaveTaskMutation
} from '@paintscout/api'
import type ApolloCache from '@wora/apollo-cache'
import { useMemo } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { createLogger } from '@paintscout/logger'

const log = createLogger('useTaskMutations')

const DEFAULT_REFETCH_QUERIES = ['searchActivities']

/**
 * Returns common useMutations for saving tasks and handles updating the caches
 */
export function useTaskMutations() {
  const [saveTaskMutation] = useSaveTaskMutation()
  const [deleteTasksMutation] = useDeleteTasksMutation()
  const [notifyChecklistAssigneesMutation] = useNotifyChecklistAssigneesMutation()
  const client = useApolloClient()

  return useMemo(
    () => ({
      async saveTask({ task, skipActivity }: { task: Task; skipActivity?: boolean }) {
        const timeOfSave = Date.now()
        const mutationId = `save-task-${task._id}`
        const result = await saveTaskMutation({
          variables: {
            task,
            skipActivity
          },
          context: {
            __mutation_id__: mutationId
          },

          optimisticResponse: {
            saveTask: {
              __typename: 'Task' as const,
              created: null,
              ...task,
              owner: task.owner
                ? {
                    __typename: 'Owner',
                    ...task.owner
                  }
                : null,
              _rev: task._rev || null // null is important, if its undefined it will break cache
            }
          },
          update(cache: ApolloCache, { data }) {
            if (data?.saveTask) {
              updateCachedTask(cache, data.saveTask)
            }
          }
        })

        removeStoredMutationsById(client, mutationId, { before: timeOfSave })

        if (!skipActivity) {
          scheduleDelayedRefetch(client, DEFAULT_REFETCH_QUERIES)
        }

        return {
          task: result.data.saveTask
        }
      },

      /**
       * Sends one grouped task-assigned notification per assignee for the tasks one checklist
       * apply just saved. Best effort: the tasks are saved, so a failure here is only logged.
       */
      async notifyChecklistAssignees(taskIds: string[]) {
        try {
          await notifyChecklistAssigneesMutation({ variables: { taskIds } })
        } catch (error) {
          // Reported, not thrown: the tasks are saved, and putTask already skipped their per-task emails,
          // so this is the only trace that the assignees were never told.
          log.error('Failed to notify checklist assignees', { error, taskIds: taskIds.join(',') })
        }
      },

      async deleteTasks(ids: string[]) {
        const result = await deleteTasksMutation({
          variables: { ids },
          optimisticResponse: {
            deleteTasks: ids.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null
            }))
          },
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              // we can fake the task document as just an object with _deleted flag
              updateCachedTaskPartially(cache, id, {
                _deleted: true
              })
            })
          }
        })

        scheduleDelayedRefetch(client, DEFAULT_REFETCH_QUERIES)

        return result
      }
    }),
    [client]
  )
}
